/**
 * Alert - 알림 배너 컴포넌트
 *
 * @param variant - 'info' | 'brand' | 'destructive' | 'success' | 'warning' 색상 변형
 * @param dismissible - true이면 닫기 버튼 표시
 * @param action - 버튼 형태의 추가 액션 (label, onClick)
 */
'use client';

import React, { useState } from 'react';
import {
  Info,
  Shield,
  AlertTriangle,
  CheckCircle,
  AlertTriangle as WarningIcon,
  X,
} from 'lucide-react';
import styles from './Alert.module.css';

export interface AlertAction {
  label: string;
  onClick: () => void;
}

export interface AlertProps {
  variant?: 'info' | 'brand' | 'destructive' | 'success' | 'warning';
  appearance?: 'light' | 'dark';
  title?: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  action?: AlertAction;
  className?: string;
}

const defaultIcons: Record<string, React.ReactNode> = {
  info: <Info size={20} />,
  brand: <Shield size={20} />,
  destructive: <AlertTriangle size={20} />,
  success: <CheckCircle size={20} />,
  warning: <WarningIcon size={20} />,
};

export default function Alert({
  variant = 'info',
  appearance: alertStyle = 'light',
  title,
  children,
  icon,
  dismissible = false,
  onDismiss,
  action,
  className = '',
}: AlertProps) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  const resolvedIcon = icon ?? defaultIcons[variant];

  const classNames = [styles.alert, styles[variant], styles[alertStyle], className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classNames} role="alert">
      {resolvedIcon && <span className={styles.icon}>{resolvedIcon}</span>}
      <div className={styles.content}>
        {title && <div className={styles.title}>{title}</div>}
        <div className={styles.description}>{children}</div>
      </div>
      {action && (
        <button type="button" className={styles.actionButton} onClick={action.onClick}>
          {action.label}
        </button>
      )}
      {dismissible && (
        <button
          type="button"
          className={styles.dismissButton}
          onClick={handleDismiss}
          aria-label="닫기"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
}
